import { Ruler } from 'lucide-react';
import { Link } from 'react-router';

import { getBrandName } from '@/data/brands';
import { getEntryVariant, isProductAvailable } from '@/data/products';
import { cn } from '@/lib/cn';
import { LIFE_STAGES, PRODUCT_LINES, STOCK_STATUS } from '@/lib/taxonomy';
import type { Product } from '@/types/catalog';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AddToCartButton } from './AddToCartButton';
import { FavoriteButton } from './FavoriteButton';
import { Price } from './Price';
import { ProductImage } from './ProductImage';
import { StarRating } from './StarRating';

const BADGE_META = {
  bestseller: { label: '🔥 Mais vendido', variant: 'primary' },
  new: { label: '✨ Novidade', variant: 'accent' },
  sale: { label: 'Oferta', variant: 'primary' },
} as const;

interface ProductCardProps {
  product: Product;
  priority?: boolean;
  className?: string;
}

export function ProductCard({ product, priority, className }: ProductCardProps) {
  const entry = getEntryVariant(product);
  const stock = STOCK_STATUS[entry.stock];
  const hasMultipleSizes = product.variants.length > 1;
  const available = isProductAvailable(product);

  return (
    <article
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-3xl border-2 border-sand-300 bg-white shadow-card hover:shadow-card-hover',
        'transition-[transform,box-shadow] duration-300 ease-[var(--ease-out-soft)] hover:-translate-y-1',
        'focus-within:ring-2 focus-within:ring-orange-500 focus-within:ring-offset-2',
        className,
      )}
    >
      {/* Badges */}
      <div className="pointer-events-none absolute top-3 left-3 z-10 flex flex-col items-start gap-1.5">
        {product.badges?.map((badge) => (
          <Badge key={badge} variant={BADGE_META[badge].variant} size="sm">
            {BADGE_META[badge].label}
          </Badge>
        ))}
        {entry.stock === 'low-stock' && (
          <Badge variant="warning" size="sm">
            ⏳ {stock.label}
          </Badge>
        )}
        {/* Sem isto, um produto totalmente esgotado ficava indistinguível de
            um disponível até o cliente chegar na página de produto. */}
        {!available && (
          <Badge variant="dark" size="sm">
            Sob encomenda
          </Badge>
        )}
      </div>

      <FavoriteButton
        productId={product.id}
        productName={product.name}
        className="absolute top-3 right-3 z-10"
      />

      {/* Image — the whole block links to the product page */}
      <Link
        to={`/produto/${product.slug}`}
        tabIndex={-1}
        aria-hidden="true"
        className="block aspect-[4/3] overflow-hidden"
      >
        <ProductImage
          product={product}
          weightLabel={entry.label}
          priority={priority ?? false}
          decorative
          className="h-full w-full"
        />
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-display text-xs font-bold tracking-wide text-caramel-600 uppercase">
            {getBrandName(product.brandId)}
          </span>
          <Badge variant="outline" size="sm" className="shrink-0">
            {PRODUCT_LINES[product.line].short}
          </Badge>
        </div>

        <h3 className="line-clamp-2 font-display text-base leading-snug font-bold text-brown-900">
          {/* Stretched link: the whole card is clickable, buttons stay on top. */}
          <Link to={`/produto/${product.slug}`} className="before:absolute before:inset-0">
            {product.name}
          </Link>
        </h3>

        {product.reviewCount > 0 && (
          <StarRating rating={product.rating} reviewCount={product.reviewCount} />
        )}

        <ul className="flex flex-wrap gap-1.5">
          {product.lifeStages.map((stage) => (
            <li key={stage}>
              <Badge variant="neutral" size="sm">
                {LIFE_STAGES[stage].emoji} {LIFE_STAGES[stage].label}
              </Badge>
            </li>
          ))}
        </ul>

        <div className="mt-auto flex items-end justify-between gap-2 pt-1">
          <Price
            variant={entry}
            {...(hasMultipleSizes ? { prefix: 'a partir de' } : {})}
            size="md"
          />
          {/* The size is already printed on the pack art; hide it when space
              is tight rather than letting it squeeze the price. */}
          <span className="hidden pb-1 text-xs font-semibold text-brown-700/70 sm:block">
            {entry.label}
          </span>
        </div>

        {/* Buttons sit above the stretched link so they stay clickable. */}
        <div className="relative z-10 flex gap-2">
          {hasMultipleSizes ? (
            // Several sizes: send the visitor to the page where they can choose.
            <Button asChild full size="sm" className="px-3">
              <Link to={`/produto/${product.slug}`}>
                <Ruler aria-hidden="true" />
                Ver opções
              </Link>
            </Button>
          ) : (
            <AddToCartButton product={product} variant={entry} full size="sm" className="px-3" />
          )}
        </div>
      </div>
    </article>
  );
}
